import React, { useState, useEffect, useCallback } from 'react';
import {
  PhotoIcon,
  DocumentIcon,
  TrashIcon,
  StarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  PlusIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';

const ObituaryAttachments = ({ obituaryId, token }) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  // Upload form state
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [filePreview, setFilePreview] = useState(null);

  // Edit form state
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const fetchAttachments = useCallback(async () => {
    try {
      const response = await fetch(`/api/obituaries/${obituaryId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Error fetching attachments:', error);
    } finally {
      setLoading(false);
    }
  }, [obituaryId, token]);

  useEffect(() => {
    fetchAttachments();
  }, [fetchAttachments]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const processFile = (file) => {
    setSelectedFile(file);
    setUploadTitle(file.name.replace(/\.[^/.]+$/, '')); // Remove extension for title

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => setFilePreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }

    setShowUploadForm(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);

      // Convert file to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(selectedFile);
      });

      const isImage = selectedFile.type.startsWith('image/');

      const payload = {
        attachmentType: isImage ? 'image' : 'document',
        title: uploadTitle || selectedFile.name,
        description: uploadDescription,
        fileName: selectedFile.name,
        fileType: selectedFile.type,
        fileData: base64,
        fileSize: selectedFile.size,
        isPrimary: attachments.filter(a => a.attachmentType === 'image').length === 0 && isImage
      };

      const response = await fetch(`/api/obituaries/${obituaryId}/attachments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        resetUploadForm();
        fetchAttachments();
      }
    } catch (error) {
      console.error('Error uploading attachment:', error);
    } finally {
      setUploading(false);
    }
  };

  const resetUploadForm = () => {
    setShowUploadForm(false);
    setSelectedFile(null);
    setUploadTitle('');
    setUploadDescription('');
    setFilePreview(null);
  };

  const handleDelete = async (attachmentId) => {
    if (!window.confirm('Are you sure you want to delete this attachment?')) return;

    try {
      const response = await fetch(`/api/obituaries/${obituaryId}/attachments/${attachmentId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        fetchAttachments();
      }
    } catch (error) {
      console.error('Error deleting attachment:', error);
    }
  };

  const handleSetPrimary = async (attachmentId) => {
    try {
      const response = await fetch(`/api/obituaries/${obituaryId}/attachments/${attachmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ isPrimary: true })
      });

      if (response.ok) {
        fetchAttachments();
      }
    } catch (error) {
      console.error('Error setting primary:', error);
    }
  };

  const handleReorder = async (attachmentId, direction) => {
    const idx = attachments.findIndex(a => a.id === attachmentId);
    if (idx === -1) return;

    const newOrder = direction === 'up' ? idx - 1 : idx + 1;
    if (newOrder < 0 || newOrder >= attachments.length) return;

    try {
      const response = await fetch(`/api/obituaries/${obituaryId}/attachments/${attachmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ sortOrder: newOrder })
      });

      if (response.ok) {
        fetchAttachments();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  const openEditModal = (attachment) => {
    setEditingAttachment(attachment);
    setEditTitle(attachment.title || '');
    setEditDescription(attachment.description || '');
  };

  const closeEditModal = () => {
    setEditingAttachment(null);
    setEditTitle('');
    setEditDescription('');
  };

  const handleUpdate = async () => {
    if (!editingAttachment) return;

    try {
      const response = await fetch(`/api/obituaries/${obituaryId}/attachments/${editingAttachment.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editTitle,
          description: editDescription
        })
      });

      if (response.ok) {
        closeEditModal();
        fetchAttachments();
      }
    } catch (error) {
      console.error('Error updating attachment:', error);
    }
  };

  const images = attachments.filter(a => a.attachmentType === 'image');
  const documents = attachments.filter(a => a.attachmentType === 'document');

  if (loading) {
    return <div className="text-gray-500 text-center py-4">Loading attachments...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Upload Zone */}
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          dragOver
            ? 'border-brand-green-400 bg-brand-green-50'
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        <input
          type="file"
          id="obituary-attachment-upload"
          className="hidden"
          accept="image/*,.pdf,.doc,.docx"
          onChange={handleFileSelect}
        />
        <label
          htmlFor="obituary-attachment-upload"
          className="cursor-pointer flex flex-col items-center"
        >
          <PlusIcon className="h-10 w-10 text-gray-400 mb-2" />
          <span className="text-sm text-gray-600">
            Drop files here or click to upload
          </span>
          <span className="text-xs text-gray-400 mt-1">
            Images (JPG, PNG) or Documents (PDF, DOC)
          </span>
        </label>
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Upload Attachment
            </h3>

            {filePreview && (
              <div className="mb-4">
                <img
                  src={filePreview}
                  alt="Preview"
                  className="w-full h-40 object-contain rounded-lg bg-gray-100"
                />
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={uploadDescription}
                  onChange={(e) => setUploadDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={resetUploadForm}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={uploading}
                className="px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors disabled:opacity-50"
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Form Modal */}
      {editingAttachment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Edit Attachment
            </h3>

            {editingAttachment.attachmentType === 'image' && (
              <div className="mb-4">
                <img
                  src={`/api/obituaries/${obituaryId}/attachments/${editingAttachment.id}/thumbnail`}
                  alt={editingAttachment.title || 'Photo'}
                  className="w-full h-40 object-contain rounded-lg bg-gray-100"
                />
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={closeEditModal}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdate}
                className="px-4 py-2 bg-brand-green-600 text-white rounded-lg hover:bg-brand-green-700 transition-colors"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Images Grid */}
      {images.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Photos</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {images.map((attachment, idx) => (
              <div
                key={attachment.id}
                className="relative group bg-gray-100 rounded-lg overflow-hidden aspect-square"
              >
                <img
                  src={`/api/obituaries/${obituaryId}/attachments/${attachment.id}/thumbnail`}
                  alt={attachment.title || 'Photo'}
                  className="w-full h-full object-cover"
                />

                {/* Primary Badge */}
                {attachment.isPrimary && (
                  <div className="absolute top-2 left-2 bg-yellow-400 text-yellow-900 px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1">
                    <StarIconSolid className="h-3 w-3" />
                    Primary
                  </div>
                )}

                {/* Actions Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEditModal(attachment)}
                      className="p-2 bg-white rounded-full text-brand-green-600 hover:bg-brand-green-50"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    {!attachment.isPrimary && (
                      <button
                        onClick={() => handleSetPrimary(attachment.id)}
                        className="p-2 bg-white rounded-full text-yellow-600 hover:bg-yellow-50"
                        title="Set as primary"
                      >
                        <StarIcon className="h-5 w-5" />
                      </button>
                    )}
                    {idx > 0 && (
                      <button
                        onClick={() => handleReorder(attachment.id, 'up')}
                        className="p-2 bg-white rounded-full text-gray-600 hover:bg-gray-50"
                        title="Move up"
                      >
                        <ArrowUpIcon className="h-5 w-5" />
                      </button>
                    )}
                    {idx < images.length - 1 && (
                      <button
                        onClick={() => handleReorder(attachment.id, 'down')}
                        className="p-2 bg-white rounded-full text-gray-600 hover:bg-gray-50"
                        title="Move down"
                      >
                        <ArrowDownIcon className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(attachment.id)}
                      className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Title */}
                {attachment.title && (
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                    <p className="text-white text-xs truncate">{attachment.title}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents List */}
      {documents.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-3">Documents</h3>
          <div className="space-y-2">
            {documents.map((attachment) => (
              <div
                key={attachment.id}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <DocumentIcon className="h-8 w-8 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {attachment.title || attachment.fileName}
                    </p>
                    {attachment.description && (
                      <p className="text-xs text-gray-500">{attachment.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openEditModal(attachment)}
                    className="text-brand-green-600 hover:text-brand-green-800"
                    title="Edit"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(attachment.id)}
                    className="text-red-600 hover:text-red-800"
                    title="Delete"
                  >
                    <TrashIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {attachments.length === 0 && (
        <div className="text-center py-4 text-gray-500 text-sm">
          No attachments yet. Upload photos or documents above.
        </div>
      )}
    </div>
  );
};

export default ObituaryAttachments;
